import type { CookieConsentConfig, CookieValue } from 'vanilla-cookieconsent';

export const getTrackingDomain = () => {
  const host = window.location.host;
  const isLocalhost = host.includes('localhost') || host.includes('.local.genius.tv');
  const isPreProd = host.includes('pre-release') || host.includes('staging');
  if (isLocalhost || isPreProd) {
    return 'pre-release.genius.tv';
  }
  return `at.genius.tv`;
};

const getCookieConsentContent = (): string => {
  return `
 <div style="display: flex; flex-direction: column; align-items: start; justify-content: center;">
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Lieber Genius Kunde, uns liegt viel daran Ihnen ein optimales Einkaufserlebnis zu bieten.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Wir verwenden auf unserer Webseite Cookies und ähnliche Tools, um Ihnen eine bestmögliche Nutzung unserer Webseite ermöglichen zu können.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Für den technischen Betrieb unserer Webseite nutzen wir Cookies, die unsere Webseite für Sie technisch zugänglich und funktionsfähig machen <strong style = "font-weight: bold;">(technisch notwendige Cookies)</strong>. Darunter fallen vor allem die wesentlichen Grundfunktionen, wie unter anderem die richtige Darstellung in Ihrem Webbrowser.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Darüber hinaus erheben wir und unsere Partner Daten (u.a. IP-Adresse, Geräte- und Browser-Informationen, Informationen zu Ihrer Nutzung unserer Webseite) und verwenden Cookies und ähnliche Technologien, um insbesondere personalisierte Werbeanzeigen einzublenden und Messwerte (u.a. Erfolgsmessung von Anzeigen) zu erfassen <strong style = "font-weight: bold;">(Cookies für Statistik & Tracking)</strong>.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Wir erheben, verarbeiten, nutzen oder speichern personenbezogene Daten für Marketing, Analyse und Personalisierung jedoch nur dann, wenn Sie uns Ihre Einwilligung erklären.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Daher benötigen wir nicht nur für die Erhebung der Daten, sondern auch für die Weiterverarbeitung unserer Daten durch unsere Partner Ihre Einwilligung.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Bei unseren <strong style="font-weight: bold;">Partnern</strong> handelt es sich um folgende Unternehmen: Google Ireland Ltd., Meta Platforms Ireland Limited (ehemals Facebook Ireland Ltd.), Microsoft Ireland Operations Limited, Pinterest Europe Limited, Criteo SA, Adform A/S, Aditiontechnologies AG, AWIN AG und The Trade Desk GmbH. Weitere Informationen zu den Diensten unserer Partner finden Sie in unserer Datenschutzerklärung.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Mit Ihrer Einwilligung akzeptieren Sie auch die Übermittlung der Daten in die USA und ggf. weitere Länder. Wir weisen darauf hin, dass nach der Rechtsprechung des Europäischen Gerichtshofs die USA derzeit kein mit der EU vergleichbares Datenschutzniveau haben und das Risiko der unbemerkten Datenverarbeitung durch staatliche Stellen besteht.</p>
    <br />
    <p style="line-height: 1.5rem; font-size: 0.875rem; text-align: start;">Ihre Einwilligung können Sie mit dem Klick auf „Alle akzeptieren“ erklären. Selbstverständlich können Sie Ihre freiwillige Einwilligung (u.a. zur Personalisierung von Webeanzeigen) jederzeit mit Wirkung für die Zukunft widerrufen oder im Cookie-Manager über Ihren Internetbrowser ändern. Klicken Sie auf „Konfigurieren“, um Cookies abzulehnen und detaillierte Einstellungen vorzunehmen. Weitere Informationen zu unseren eigensetzten Cookies, zur Datenübermittlung und weiteren Einstellungsmöglichkeiten finden Sie hier und auch in unserer Datenschutzerklärung.</p>
    <br />
</div>
  `;
};
// global.d.ts
export const getCookieValue = (cookieName: string): string | null => {
  if (typeof document === 'undefined') {
    // Prevent execution on the server
    return null;
  }

  const name = `${cookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);

  const cookieArray = decodedCookie.split(';');

  for (const cookie of cookieArray) {
    const trimmedCookie = cookie.trim();

    if (trimmedCookie.startsWith(name)) {
      const result = trimmedCookie.substring(name.length);
      return result;
    }
  }

  return null;
};

interface Window {
  _etracker?: {
    areCookiesEnabled: () => boolean;
    enableCookies: (domain: string) => void;
    disableCookies: (domain: string) => void;
  };
}

const getConfig = (countryCode: string, formatMessage: (msg: { id: string; defaultMessage: string }) => string) => {
  const config: CookieConsentConfig = {
    root: 'footer',
    autoShow: true,
    disablePageInteraction: true,
    //hideFromBots: true,
    // mode: 'opt-in',
    // revision: 0,
    cookie: {
      name: `cc_cookie_${countryCode}`,
      domain: location.hostname,
      path: '/',
      sameSite: 'Lax',
      expiresAfterDays: 7,
    },
    /**
     * Callback functions
     */
    onFirstConsent: ({ cookie }) => {
      console.log('onFirstConsent fired', cookie);
      location.reload();
    },
    onConsent: ({ cookie }) => {
      try {
        if (cookie?.services?.ads) {
          const etrackerExists = cookie.services.ads.find(
            (cookieName) => cookieName && cookieName.toLowerCase().includes('etracker'),
          );
          if (!etrackerExists && window._etracker) {
            window._etracker.disableCookies(getTrackingDomain());
          }
          window._etracker && console.log(`Etracker Cookies disabled? ${window._etracker.areCookiesEnabled()}`);
        }
      } catch (e) {
        console.log('something went wrong while updating cookies:', e);
      }
      console.log('onConsent fired!', cookie);
      return {};
    },
    onChange: ({ changedCategories, changedServices }) => {
      console.log('onChange fired!', changedCategories, changedServices);
    },
    onModalReady: ({ modalName }) => {
      console.log('ready:', modalName);
    },
    onModalShow: ({ modalName }) => {
      console.log('visible:', modalName);
    },
    onModalHide: ({ modalName }) => {
      console.log('hidden:', modalName);
    },
    // https://cookieconsent.orestbida.com/reference/configuration-reference.html#guioptions
    guiOptions: {
      consentModal: {
        layout: 'bar',
        position: 'top center',
        equalWeightButtons: true,
        flipButtons: true,
      },
      preferencesModal: {
        layout: 'bar',
        equalWeightButtons: true,
        flipButtons: true,
      },
    },
    categories: {
      necessary: {
        enabled: true, // this category is enabled by default
        readOnly: true, // this category cannot be disabled
        services: {
          'apay-session-set': {
            label: 'Amazon Pay',
            onAccept: () => {},
            onReject: () => {},
          },
          shop: {
            label: 'Selected Shop',
            onAccept: () => {},
            onReject: () => {},
          },
          csrf_token: {
            label: 'CSRF Token',
            onAccept: () => {},
            onReject: () => {},
          },
          cookiePreferences: {
            label: 'Cookie Settings',
            onAccept: () => {},
            onReject: () => {},
          },
          wbm_tag_manager: {
            label: 'Google Tag Manager',
            onAccept: () => {},
            onReject: () => {},
          },
          'x-cache-context-hash': {
            label: 'Individual Prices',
            onAccept: () => {},
            onReject: () => {},
          },
          'paypal-cookies': {
            label: 'PayPal Payments',
            onAccept: () => {},
            onReject: () => {},
          },
          session: {
            label: 'Session',
            onAccept: () => {},
            onReject: () => {},
          },
          locale: {
            label: 'Locale',
            onAccept: () => {},
            onReject: () => {},
          },
        },
      },
      // analytics: {
      //   enabled: true,
      //   autoClear: {
      //     cookies: [
      //       {
      //         name: /^_ga/, // regex: match all cookies starting with '_ga'
      //       },
      //       {
      //         name: '_gid', // string: exact cookie name
      //       },
      //     ],
      //   },
      //   // https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
      // },
      ads: {
        autoClear: {
          cookies: [
            {
              name: 'google_ads_conversion_*', // regex: match all cookies starting with 'google_ads_conversion_'
            },
            {
              name: '_ga', // string: exact cookie name
            },
          ],
        },
        services: {
          'awin-tracking': {
            label: 'Awin',
            onAccept: () => {},
            onReject: () => {},
          },
          'criteo-tracking': {
            label: 'Criteo',
            onAccept: () => {},
            onReject: () => {},
          },
          etracker: {
            label: 'Etracker',
            onAccept: () => {
              if (window._etracker && !window._etracker.areCookiesEnabled()) {
                window._etracker.enableCookies(getTrackingDomain());
              }
              window._etracker && console.log(`Etracker Cookies disabled? ${window._etracker.areCookiesEnabled()}`);
            },
            onReject: () => {
              if (window._etracker) {
                window._etracker.disableCookies(getTrackingDomain());
              }
              window._etracker && console.log(`Etracker Cookies disabled? ${window._etracker.areCookiesEnabled()}`);
            },
          },
          'bing-ads-tracking': {
            label: 'Microsoft Advertising/ Microsoft Clarity',
            onAccept: () => {},
            onReject: () => {},
          },
          'pinterest-tracking': {
            label: 'Pinterest',
            onAccept: () => {},
            onReject: () => {},
          },
          'tiktok-pixel-tracking': {
            label: 'TikTok Pixel',
            onAccept: () => {},
            onReject: () => {},
          },
          'guenstiger-tracking': {
            label: 'günstiger.de',
            onAccept: () => {},
            onReject: () => {},
          },
          'solute-tracking': {
            label: 'Solute (billiger.de)',
            onAccept: () => {},
            onReject: () => {},
          },
        },
      },
    },
    language: {
      default: 'de',
      translations: {
        de: {
          consentModal: {
            title: `<p style="line-height: 1.5rem; font-size: 24px; text-align: left; margin-top: 10px; margin-bottom: 20px; font-weight: 500;">Ihre Privatsphäre ist uns wichtig ! - Cookie Einstellungen</p>`,
            description: getCookieConsentContent(),
            acceptAllBtn: formatMessage({ id: 'accept.all', defaultMessage: 'Accept all' }),
            acceptNecessaryBtn: formatMessage({ id: 'reject.all', defaultMessage: 'Reject all' }),
            showPreferencesBtn: formatMessage({
              id: 'manage.individual.preferences',
              defaultMessage: 'Manage Individual preferences',
            }),
            footer: `
                        <a href="/impressum">Impressum</a>
                        <a href="/datenschutz">Datenschutzrichtlinie</a>
                    `,
          },
          preferencesModal: {
            title: formatMessage({
              id: 'Cookie-Einstellungen verwalten',
              defaultMessage: 'Cookie-Einstellungen verwalten',
            }),
            savePreferencesBtn: formatMessage({
              id: 'save.settings',
              defaultMessage: 'Save settings',
            }),
            closeIconLabel: 'Close modal',
            serviceCounterLabel: 'Service|Services',
            sections: [
              {
                title: formatMessage({
                  id: 'Ihre Datenschutzoptionen',
                  defaultMessage: 'Ihre Datenschutzoptionen',
                }),
                description: formatMessage({
                  id: 'preferences in context',
                  defaultMessage: `In diesem Bereich können Sie einige Präferenzen im Zusammenhang mit der Verarbeitung Ihrer persönlichen Daten äußern. Sie können Ihre getroffenen Entscheidungen jederzeit überprüfen und ändern, indem Sie dieses Panel über den bereitgestellten Link erneut aufrufen. Um Ihre Zustimmung zu den unten beschriebenen spezifischen Verarbeitungsaktivitäten zu verweigern, schalten Sie die Schalter auf „Aus“ oder verwenden Sie die Schaltfläche „Alle ablehnen“ und bestätigen Sie, dass Sie Ihre Auswahl speichern möchten.`,
                }),
              },
              {
                title: formatMessage({
                  id: 'technisch erforderlich',
                  defaultMessage: 'Technisch erforderlich',
                }),
                description: formatMessage({
                  id: 'proper functioning',
                  defaultMessage:
                    'Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich und können nicht deaktiviert werden.',
                }),

                //this field will generate a toggle linked to the 'necessary' category
                linkedCategory: 'necessary',
              },
              {
                title: formatMessage({
                  id: 'Statistiken und Tracking',
                  defaultMessage: 'Statistiken und Tracking',
                }),
                description: formatMessage({
                  id: 'Cookies werden verwendet',
                  defaultMessage:
                    'Diese Cookies werden verwendet, um Werbebotschaften relevanter für Sie und Ihre Interessen zu machen. Ziel ist es, Anzeigen anzuzeigen, die für den einzelnen Nutzer relevant und ansprechend sind und dadurch für Publisher und Drittwerbetreibende wertvoller sind.',
                }),
                linkedCategory: 'ads',
              },
            ],
          },
        },
      },
    },
  };
  return config;
};
export default getConfig;
